import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import ImageUpload from '@/components/ImageUpload';
import RichTextEditor from '@/components/RichTextEditor';
import SchedulingControls from '@/components/SchedulingControls';
import SchedulePickerDialog, { formatLisbon } from '@/components/SchedulePickerDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Newspaper, 
  Plus, 
  Edit, 
  Trash2, 
  Loader2,
  ArrowLeft,
  Save,
  Eye,
  Image,
  Clock,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

export default function AdminArticleManager() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]); // [{id, name, slug, description, article_count}]
  const [coverStatus, setCoverStatus] = useState({}); // {id: {status, reason}}
  const [auditRunning, setAuditRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  
  // Form state
  const [articleForm, setArticleForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    cover_image: '',
    reading_time: 5
  });
  const [editingArticle, setEditingArticle] = useState(null);

  // Scheduling: staged UTC ISO for the current form (null = publish now)
  const [scheduledAt, setScheduledAt] = useState(null);
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);

  // CSV Import state
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvPublish, setCsvPublish] = useState(true);

  // Em Destaque (admin-pinned featured articles for /+prazer)
  const FEATURED_CMS_KEY = 'prazer.em_destaque.article_ids';
  const [featuredIds, setFeaturedIds] = useState(['', '', '']);
  const [featuredExists, setFeaturedExists] = useState(false);
  const [featuredSaving, setFeaturedSaving] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadArticles();
    loadCategories();
    loadCoverStatus();
    loadFeatured();
  }, [user, navigate]);

  const loadFeatured = async () => {
    try {
      const res = await api.get('/admin/cms/content?page=prazer');
      const block = (res.data || []).find((b) => b.key === FEATURED_CMS_KEY);
      if (block && typeof block.content === 'string' && block.content.trim()) {
        const ids = block.content.split(',').map((s) => s.trim()).filter(Boolean);
        setFeaturedIds([ids[0] || '', ids[1] || '', ids[2] || '']);
        setFeaturedExists(true);
      } else {
        setFeaturedIds(['', '', '']);
        setFeaturedExists(!!block);
      }
    } catch {
      // Non-blocking: featured is optional.
    }
  };

  const handleSaveFeatured = async () => {
    const cleaned = featuredIds.map((v) => (v || '').trim()).filter(Boolean);
    const uniqueCleaned = Array.from(new Set(cleaned));
    if (uniqueCleaned.length !== cleaned.length) {
      toast.error('Não podes escolher o mesmo artigo em mais do que um destaque');
      return;
    }
    if (cleaned.length === 0) {
      toast.error('Escolhe pelo menos um artigo ou usa "Repor automático"');
      return;
    }
    setFeaturedSaving(true);
    try {
      const content = cleaned.join(',');
      if (featuredExists) {
        await api.put(`/admin/cms/content/${FEATURED_CMS_KEY}`, { content });
      } else {
        await api.post('/admin/cms/content', {
          key: FEATURED_CMS_KEY,
          content,
          content_type: 'text',
          page: 'prazer',
          section: 'em_destaque',
          label: '+Prazer — Em Destaque (IDs ordenados, separados por vírgula)',
        });
        setFeaturedExists(true);
      }
      toast.success('Destaques guardados');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao guardar destaques'));
    } finally {
      setFeaturedSaving(false);
    }
  };

  const handleResetFeatured = async () => {
    if (!featuredExists) {
      setFeaturedIds(['', '', '']);
      toast.success('Seleção limpa');
      return;
    }
    if (!window.confirm('Repor o modo automático (por número de views)? A seleção manual será apagada.')) return;
    setFeaturedSaving(true);
    try {
      await api.delete(`/admin/cms/content/${FEATURED_CMS_KEY}`);
      setFeaturedIds(['', '', '']);
      setFeaturedExists(false);
      toast.success('Reposto para modo automático');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao repor destaques'));
    } finally {
      setFeaturedSaving(false);
    }
  };

  const loadCoverStatus = async () => {
    setAuditRunning(true);
    try {
      const res = await api.get('/admin/articles/cover-image-audit');
      const map = {};
      for (const it of (res.data?.items || [])) {
        map[it.id] = { status: it.status, reason: it.reason };
      }
      setCoverStatus(map);
    } catch {
      // Non-blocking: audit is informational.
    } finally {
      setAuditRunning(false);
    }
  };

  const loadArticles = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/articles');
      setArticles(res.data);
    } catch (error) {
      toast.error('Erro ao carregar artigos');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await api.get('/admin/article-categories');
      const cats = Array.isArray(res.data) ? res.data : [];
      setCategories(cats);
      setArticleForm((prev) => (
        prev.category ? prev : { ...prev, category: cats[0]?.slug || '' }
      ));
    } catch (error) {
      toast.error('Erro ao carregar categorias');
    }
  };

  const resetForm = () => {
    setArticleForm({
      title: '',
      excerpt: '',
      content: '',
      category: categories[0]?.slug || '',
      cover_image: '',
      reading_time: 5
    });
    setEditingArticle(null);
    setScheduledAt(null);
  };

  const openCreateTab = () => {
    resetForm();
    setActiveTab('create');
  };

  const openEditTab = async (article) => {
    const fallbackCategory = categories[0]?.slug || '';
    try {
      const res = await api.get(`/articles/${article.id}`);
      const full = res.data;
      setEditingArticle(full);
      setArticleForm({
        title: full.title,
        excerpt: full.excerpt || '',
        content: full.content || '',
        category: full.category || fallbackCategory,
        cover_image: full.cover_image || '',
        reading_time: full.reading_time || 5
      });
      setScheduledAt(full.scheduled_at || null);
    } catch {
      setEditingArticle(article);
      setArticleForm({
        title: article.title,
        excerpt: article.excerpt || '',
        content: article.content || '',
        category: article.category || fallbackCategory,
        cover_image: article.cover_image || '',
        reading_time: article.reading_time || 5
      });
      setScheduledAt(article.scheduled_at || null);
    }
    setActiveTab('create');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!articleForm.title || !stripHtml(articleForm.content).trim()) {
      toast.error('Preenche o título e o conteúdo');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...articleForm };
      if (scheduledAt) payload.scheduled_at = scheduledAt;
      if (editingArticle) {
        await api.put(`/admin/articles/${editingArticle.id}`, payload);
        toast.success(scheduledAt ? `Agendado para ${formatLisbon(scheduledAt)}` : 'Artigo atualizado');
      } else {
        await api.post('/admin/articles', payload);
        toast.success(scheduledAt ? `Agendado para ${formatLisbon(scheduledAt)}` : 'Artigo publicado');
      }
      
      resetForm();
      setActiveTab('list');
      loadArticles();
      loadCoverStatus();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao guardar artigo'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!confirm('Eliminar este artigo? Esta ação não pode ser revertida.')) return;
    
    try {
      await api.delete(`/admin/articles/${articleId}`);
      toast.success('Artigo eliminado');
      loadArticles();
    } catch (error) {
      toast.error('Erro ao eliminar artigo');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const estimateReadingTime = (content) => {
    const words = content.split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  // CSV Import handlers
  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/admin/articles/csv-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCsvPreview(res.data);
      setActiveTab('import');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao processar CSV'));
    }
  };

  const handleCsvImport = async () => {
    if (!csvPreview?.articles?.length) return;
    setCsvImporting(true);
    try {
      const res = await api.post('/admin/articles/csv-import', {
        articles: csvPreview.articles,
        is_published: csvPublish
      });
      toast.success(res.data.message);
      setCsvPreview(null);
      setActiveTab('list');
      loadArticles();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro na importação'));
    } finally {
      setCsvImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-ruby/20 flex items-center justify-center">
                <Newspaper className="w-6 h-6 text-ruby" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Artigos Educativos</h1>
                <p className="text-muted-foreground">Secção + Prazer</p>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-surface border border-border mb-6">
            <TabsTrigger value="list" className="data-[state=active]:bg-ruby">
              Lista de Artigos
            </TabsTrigger>
            <TabsTrigger value="create" className="data-[state=active]:bg-ruby">
              {editingArticle ? 'Editar Artigo' : 'Novo Artigo'}
            </TabsTrigger>
            <TabsTrigger value="import" className="data-[state=active]:bg-ruby">
              Importar CSV
            </TabsTrigger>
            <TabsTrigger value="featured" className="data-[state=active]:bg-ruby" data-testid="tab-em-destaque">
              Em Destaque
            </TabsTrigger>
          </TabsList>

          {/* Articles List */}
          <TabsContent value="list">
            <div className="flex justify-between items-center mb-4">
              <p className="text-muted-foreground">{articles.length} artigos publicados</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={loadCoverStatus}
                  className="border-border"
                  disabled={auditRunning}
                  data-testid="cover-image-audit-btn"
                  title="Reavaliar o estado das imagens de capa"
                >
                  {auditRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Image className="w-4 h-4 mr-2" />}
                  Verificar capas
                </Button>
                <Button variant="outline" onClick={() => setActiveTab('import')} className="border-border" data-testid="csv-import-tab-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />Importar CSV
                </Button>
                <Button onClick={openCreateTab} className="bg-ruby hover:bg-ruby-light">
                  <Plus className="w-4 h-4 mr-2" />Novo Artigo
                </Button>
              </div>
            </div>

            {(() => {
              const values = Object.values(coverStatus);
              if (values.length === 0) return null;
              const missing = values.filter(v => v.status === 'missing').length;
              const empty = values.filter(v => v.status === 'empty').length;
              const external = values.filter(v => v.status === 'external').length;
              const problems = missing + empty;
              if (problems === 0 && external === 0) return null;
              return (
                <div
                  className="mb-4 p-3 rounded-lg border border-amber-500/40 bg-amber-500/10 text-sm text-amber-100 flex items-start gap-2"
                  data-testid="cover-image-audit-summary"
                >
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <strong>Auditoria de capas:</strong>{' '}
                    {missing > 0 && <>{missing} ficheiro(s) em falta · </>}
                    {empty > 0 && <>{empty} sem capa · </>}
                    {external > 0 && <>{external} URL externo(s)</>}
                    <div className="text-xs opacity-80 mt-0.5">
                      Os artigos afetados mostram um marcador visual ao lado do título. Usa <em>Editar</em> para carregar uma nova capa persistente.
                    </div>
                  </div>
                </div>
              );
            })()}

            {articles.length === 0 ? (
              <Card className="bg-surface border-border">
                <CardContent className="py-16 text-center">
                  <Newspaper className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-foreground mb-2">Nenhum artigo publicado</h3>
                  <Button onClick={openCreateTab} className="bg-ruby hover:bg-ruby-light mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Publicar Primeiro Artigo
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {articles.map((article) => {
                  const cover = coverStatus[article.id];
                  const coverBad = cover && (cover.status === 'missing' || cover.status === 'empty');
                  return (
                  <Card key={article.id} className="bg-surface border-border" data-testid={`admin-article-row-${article.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {/* Cover */}
                        <div className="w-20 h-14 rounded overflow-hidden shrink-0 bg-white/5 relative">
                          {article.cover_image ? (
                            <img
                              src={article.cover_image}
                              alt={article.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const sib = e.currentTarget.nextElementSibling;
                                if (sib) sib.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className="w-full h-full items-center justify-center absolute inset-0"
                            style={{ display: article.cover_image ? 'none' : 'flex' }}
                          >
                            <Image className="w-6 h-6 text-muted-foreground" />
                          </div>
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-foreground truncate">{article.title}</h3>
                            {cover?.status === 'missing' && (
                              <Badge variant="outline" className="border-red-500/50 text-red-300 bg-red-500/10 shrink-0" title={cover.reason} data-testid={`cover-status-${article.id}`}>
                                <AlertCircle className="w-3 h-3 mr-1" />Capa em falta
                              </Badge>
                            )}
                            {cover?.status === 'empty' && (
                              <Badge variant="outline" className="border-amber-500/50 text-amber-300 bg-amber-500/10 shrink-0" title={cover.reason} data-testid={`cover-status-${article.id}`}>
                                <AlertCircle className="w-3 h-3 mr-1" />Sem capa
                              </Badge>
                            )}
                            {cover?.status === 'external' && (
                              <Badge variant="outline" className="border-blue-500/50 text-blue-300 bg-blue-500/10 shrink-0" title={cover.reason} data-testid={`cover-status-${article.id}`}>
                                URL externo
                              </Badge>
                            )}
                            {cover?.status === 'valid' && (
                              <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 shrink-0" data-testid={`cover-status-${article.id}`}>
                                <CheckCircle className="w-3 h-3 mr-1" />Capa OK
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <Badge variant="outline" className="border-border">
                              {article.category_name || categories.find(c => c.slug === article.category)?.name || article.category}
                            </Badge>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {article.reading_time} min
                            </span>
                            <span>{formatDate(article.created_at)}</span>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2 shrink-0 items-center flex-wrap">
                          <SchedulingControls
                            resource="articles"
                            item={article}
                            publishedFlag={(it) => !!it.is_published}
                            onChanged={loadArticles}
                          />
                          {coverBad && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditTab(article)}
                              className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                              data-testid={`replace-cover-${article.id}`}
                              title="Substituir imagem de capa"
                            >
                              <Image className="w-4 h-4 mr-1" />Substituir
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`/prazer/${article.slug || article.id}`, '_blank')}
                            className="border-border"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditTab(article)}
                            className="border-border"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(article.id)}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Create/Edit Article */}
          <TabsContent value="create">
            <Card className="bg-surface border-border">
              <CardHeader>
                <CardTitle className="text-foreground">
                  {editingArticle ? `Editar: ${editingArticle.title}` : 'Novo Artigo'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Título *</Label>
                      <Input
                        value={articleForm.title}
                        onChange={(e) => setArticleForm({...articleForm, title: e.target.value})}
                        placeholder="Título do artigo"
                        className="bg-background border-border"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Categoria *</Label>
                      <Select 
                        value={articleForm.category} 
                        onValueChange={(v) => setArticleForm({...articleForm, category: v})}
                      >
                        <SelectTrigger className="bg-background border-border" data-testid="article-category-select">
                          <SelectValue placeholder="Seleciona uma categoria" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.slug} value={cat.slug}>{cat.name}</SelectItem>
                          ))}
                          {/* Preserve legacy/unknown category so editing existing articles doesn't lose the value */}
                          {articleForm.category && !categories.some(c => c.slug === articleForm.category) && (
                            <SelectItem key={articleForm.category} value={articleForm.category}>
                              {articleForm.category} (categoria removida)
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {categories.length === 0 && (
                        <p className="text-xs text-muted-foreground">
                          Nenhuma categoria definida. Cria categorias em <button type="button" onClick={() => navigate('/admin/article-categories')} className="text-ruby underline">Gerir Categorias</button>.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Resumo / Excerto</Label>
                    <Textarea
                      value={articleForm.excerpt}
                      onChange={(e) => setArticleForm({...articleForm, excerpt: e.target.value})}
                      placeholder="Breve descrição do artigo"
                      className="bg-background border-border"
                      maxLength={300}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Conteúdo *</Label>
                    <RichTextEditor
                      content={articleForm.content}
                      onChange={(html) => {
                        const plain = stripHtml(html);
                        setArticleForm({
                          ...articleForm, 
                          content: html,
                          reading_time: estimateReadingTime(plain)
                        });
                      }}
                      placeholder="Escreve o artigo aqui..."
                      minHeight="400px"
                    />
                    {articleForm.content && (
                      <p className="text-xs text-muted-foreground">
                        ~{stripHtml(articleForm.content).split(/\s+/).filter(Boolean).length} palavras • 
                        ~{articleForm.reading_time} min de leitura
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ImageUpload
                      value={articleForm.cover_image}
                      onChange={(url) => setArticleForm({...articleForm, cover_image: url})}
                      label="Imagem de Capa"
                      aspectRatio="aspect-video"
                      recommendedSize="1600×900 · 16:9 · até 250 KB"
                      hint="Formato horizontal cinemático. Boa para stock photos."
                    />
                    <div className="space-y-2">
                      <Label>Tempo de Leitura (minutos)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={articleForm.reading_time}
                        onChange={(e) => setArticleForm({...articleForm, reading_time: parseInt(e.target.value) || 5})}
                        className="bg-background border-border"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 items-center flex-wrap">
                    {scheduledAt && (
                      <div className="text-xs text-amber-300 mr-auto bg-amber-500/10 border border-amber-500/40 rounded px-3 py-1.5" data-testid="article-scheduled-preview">
                        A guardar como agendado · {formatLisbon(scheduledAt)}
                        <button type="button" className="ml-2 underline hover:text-amber-200" onClick={() => setScheduledAt(null)} data-testid="article-clear-schedule">
                          limpar
                        </button>
                      </div>
                    )}
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        resetForm();
                        setActiveTab('list');
                      }}
                      className="border-border"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setSchedulePickerOpen(true)}
                      className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                      data-testid="article-open-schedule"
                    >
                      {scheduledAt ? 'Alterar agendamento' : 'Agendar'}
                    </Button>
                    <Button type="submit" disabled={saving} className="bg-ruby hover:bg-ruby-light" data-testid="admin-article-submit">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      {scheduledAt
                        ? (editingArticle ? 'Guardar Agendamento' : 'Agendar Publicação')
                        : (editingArticle ? 'Guardar Alterações' : 'Publicar Artigo')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CSV Import Tab */}
          <TabsContent value="import">
            <Card className="bg-surface border-border">
              <CardContent className="p-6">
                {!csvPreview ? (
                  /* Upload Area */
                  <div className="text-center py-12">
                    <FileSpreadsheet className="w-16 h-16 text-ruby/40 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">Importar Artigos via CSV</h3>
                    <p className="text-muted-foreground mb-2 max-w-md mx-auto">
                      Carregue um ficheiro CSV com as colunas <strong>title</strong> e <strong>content</strong> para importar vários artigos de uma vez.
                    </p>
                    <p className="text-xs text-muted-foreground mb-6 max-w-md mx-auto">
                      Colunas opcionais: <em>category</em>, <em>excerpt</em>. O conteúdo pode incluir HTML.
                    </p>
                    <div className="flex flex-col items-center gap-4">
                      <input
                        type="file"
                        accept=".csv"
                        id="csv-upload"
                        className="hidden"
                        onChange={handleCsvUpload}
                      />
                      <Button
                        onClick={() => document.getElementById('csv-upload')?.click()}
                        className="bg-ruby hover:bg-ruby-light px-8 py-5"
                        data-testid="csv-upload-btn"
                      >
                        <Upload className="w-5 h-5 mr-2" />
                        Selecionar Ficheiro CSV
                      </Button>
                    </div>

                    {/* CSV Format Example */}
                    <div className="mt-8 max-w-lg mx-auto text-left">
                      <p className="text-sm font-medium text-muted-foreground mb-2">Exemplo de formato CSV:</p>
                      <pre className="bg-background border border-border rounded-lg p-4 text-xs text-muted-foreground overflow-x-auto">
{`title,content,category,excerpt
"Título do Artigo","<p>Conteúdo em HTML...</p>","Intimidade","Resumo breve"
"Outro Artigo","<p>Mais conteúdo...</p>","Dicas",""`.trim()}
                      </pre>
                    </div>
                  </div>
                ) : (
                  /* Preview Area */
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-semibold text-foreground">Pré-visualização da Importação</h3>
                      <Button variant="outline" onClick={() => setCsvPreview(null)} className="border-border" size="sm">
                        <X className="w-4 h-4 mr-2" />Cancelar
                      </Button>
                    </div>

                    {/* Summary */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                      <div className="bg-background border border-border rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-foreground">{csvPreview.total_rows}</p>
                        <p className="text-xs text-muted-foreground">Linhas no CSV</p>
                      </div>
                      <div className="bg-background border border-green-500/30 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-green-400">{csvPreview.valid}</p>
                        <p className="text-xs text-muted-foreground">Prontos a importar</p>
                      </div>
                      <div className="bg-background border border-red-500/30 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-red-400">{csvPreview.errors?.length || 0}</p>
                        <p className="text-xs text-muted-foreground">Com erros</p>
                      </div>
                    </div>

                    {/* Errors */}
                    {csvPreview.errors?.length > 0 && (
                      <div className="mb-6 bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-red-400 flex items-center gap-2 mb-2">
                          <AlertCircle className="w-4 h-4" />Erros encontrados
                        </h4>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {csvPreview.errors.map((err, i) => (
                            <p key={i} className="text-xs text-red-300">
                              Linha {err.row}: {err.error}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Articles Preview */}
                    {csvPreview.articles?.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-muted-foreground mb-3">
                          Artigos a importar ({csvPreview.articles.length})
                        </h4>
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {csvPreview.articles.map((a, i) => (
                            <div key={i} className="flex items-center gap-3 bg-background border border-border rounded-lg p-3">
                              <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-foreground text-sm truncate">{a.title}</p>
                                <p className="text-xs text-muted-foreground truncate">{a.excerpt}</p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Badge variant="outline" className="border-border text-xs">{a.category}</Badge>
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="w-3 h-3" />{a.reading_time}m
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Import Options & Button */}
                    {csvPreview.valid > 0 && (
                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                          <input
                            type="checkbox"
                            checked={csvPublish}
                            onChange={(e) => setCsvPublish(e.target.checked)}
                            className="rounded"
                          />
                          Publicar imediatamente
                        </label>
                        <Button
                          onClick={handleCsvImport}
                          disabled={csvImporting}
                          className="bg-ruby hover:bg-ruby-light px-8"
                          data-testid="csv-confirm-import-btn"
                        >
                          {csvImporting ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Importar {csvPreview.valid} Artigos
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Em Destaque — admin picks 3 ordered published articles for /+prazer */}
          <TabsContent value="featured">
            <Card className="bg-surface border-border" data-testid="featured-editor">
              <CardHeader>
                <CardTitle className="text-foreground">Em Destaque (+Prazer)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Escolhe até três artigos publicados para a secção <strong>Em Destaque</strong> na página
                  pública <code className="text-ruby">/+prazer</code>. A ordem em que os escolhes é a
                  mesma em que aparecem. Se deixares em modo automático (ou se algum artigo escolhido
                  ficar despublicado), o site preenche automaticamente com os artigos mais vistos.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[0, 1, 2].map((idx) => {
                    const publishedArticles = articles.filter((a) => a.is_published);
                    return (
                      <div key={idx} className="space-y-2">
                        <Label className="text-foreground">{`${idx + 1}º Destaque`}</Label>
                        <Select
                          value={featuredIds[idx] || 'none'}
                          onValueChange={(v) => {
                            const next = [...featuredIds];
                            next[idx] = v === 'none' ? '' : v;
                            setFeaturedIds(next);
                          }}
                        >
                          <SelectTrigger
                            className="bg-background border-border"
                            data-testid={`featured-select-${idx + 1}`}
                          >
                            <SelectValue placeholder="Selecionar artigo..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— Vazio —</SelectItem>
                            {publishedArticles.map((a) => (
                              <SelectItem key={a.id} value={a.id}>
                                {a.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <Button
                    onClick={handleSaveFeatured}
                    disabled={featuredSaving}
                    className="bg-ruby hover:bg-ruby-light"
                    data-testid="featured-save-btn"
                  >
                    {featuredSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Guardar Destaques
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleResetFeatured}
                    disabled={featuredSaving}
                    className="border-border"
                    data-testid="featured-reset-btn"
                  >
                    Repor automático (por views)
                  </Button>
                  {featuredExists ? (
                    <Badge variant="secondary" className="bg-ruby/15 text-ruby">
                      Modo manual ativo
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-white/5 text-muted-foreground">
                      Modo automático (por views)
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <SchedulePickerDialog
        open={schedulePickerOpen}
        onOpenChange={setSchedulePickerOpen}
        description={articleForm.title || (editingArticle ? editingArticle.title : 'Novo Artigo')}
        initialIso={scheduledAt}
        confirmLabel="Confirmar agendamento"
        onConfirm={async (iso) => {
          setScheduledAt(iso);
          setSchedulePickerOpen(false);
        }}
      />
    </div>
  );
}
